import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './Toolboxen.css';

const Toolboxen = () => {
  const [toolboxen, setToolboxen] = useState([]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [verzendenBezig, setVerzendenBezig] = useState(false);

  // Upload form
  const [uploadData, setUploadData] = useState({ titel: '', omschrijving: '', afdeling_ids: [] });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [tbRes, afdRes, instRes] = await Promise.all([
        api.get('/toolboxen'),
        api.get('/afdelingen'),
        api.get('/instellingen/bedrijf').catch(() => ({ data: {} }))
      ]);
      setToolboxen(tbRes.data.toolboxen || []);
      setAfdelingen(afdRes.data.afdelingen || []);
      setDefaultEmail(instRes.data?.toolbox_email || '');
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { alert('Selecteer een PDF'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', uploadFile);
      fd.append('titel', uploadData.titel);
      fd.append('omschrijving', uploadData.omschrijving || '');
      fd.append('afdeling_ids', JSON.stringify(uploadData.afdeling_ids));
      await api.post('/toolboxen', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowUploadModal(false);
      setUploadData({ titel: '', omschrijving: '', afdeling_ids: [] });
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadAll();
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (tb) => {
    setSelected(tb);
    setShowDetailModal(true);
    // Probeer laatst gebruikte email voor te vullen (als override leeg)
    let lastEmail = '';
    try { lastEmail = localStorage.getItem('toolbox_last_email') || ''; } catch (e) {}
    setEmailOverride(lastEmail && lastEmail !== defaultEmail ? lastEmail : '');
    try {
      const res = await api.get(`/toolboxen/${tb.id}`);
      setDetail(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleVerstuur = async () => {
    if (!selected) return;
    if (!defaultEmail && !emailOverride) {
      alert('Geen email-adres ingesteld. Vul er een in of stel een vast adres in via Instellingen.');
      return;
    }
    const targetEmail = emailOverride || defaultEmail;
    if (!window.confirm(`Toolbox versturen naar ${targetEmail}?`)) return;
    setVerzendenBezig(true);
    try {
      const body = emailOverride ? { email_override: emailOverride } : {};
      const res = await api.post(`/toolboxen/${selected.id}/verstuur`, body);
      // Sla het verzonden email-adres lokaal op zodat we hem volgende keer voorvullen
      try { localStorage.setItem('toolbox_last_email', res.data.email || targetEmail); } catch (e) {}
      alert('Verzonden naar ' + (res.data.email || targetEmail));
      loadAll();
      setShowDetailModal(false);
    } catch (err) {
      alert('Fout bij versturen: ' + (err.response?.data?.message || err.message));
    } finally {
      setVerzendenBezig(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Toolbox verwijderen? Dit kan niet ongedaan worden gemaakt.')) return;
    try {
      await api.delete(`/toolboxen/${id}`);
      setToolboxen(prev => prev.filter(t => t.id !== id));
      setShowDetailModal(false);
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    }
  };

  const openPdf = async (id) => {
    try {
      const res = await api.get(`/toolboxen/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('Fout bij ophalen PDF');
    }
  };

  const openPdfVolledig = async (id) => {
    try {
      const res = await api.get(`/toolboxen/${id}/pdf-volledig`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('Fout bij ophalen volledige PDF: ' + (err.response?.data?.message || err.message));
    }
  };

  const toggleAfdeling = (afdId) => {
    setUploadData(prev => ({
      ...prev,
      afdeling_ids: prev.afdeling_ids.includes(afdId)
        ? prev.afdeling_ids.filter(x => x !== afdId)
        : [...prev.afdeling_ids, afdId]
    }));
  };

  const afdelingNamen = (ids) => {
    if (!ids || ids.length === 0) return 'Alle afdelingen';
    return afdelingen.filter(a => ids.includes(a.id)).map(a => a.naam).join(', ');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Laden...</div>;

  return (
    <div className="toolboxen-page" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>📚 Toolboxen</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Toolbox meetings beheren en versturen</p>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="btn-primary" style={{ padding: '10px 20px' }}>
          ➕ Nieuwe Toolbox
        </button>
      </div>

      {!defaultEmail && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 14 }}>
          ⚠️ Er is nog geen vast email-adres voor toolboxen ingesteld. Stel deze in via <strong>Instellingen → Bedrijf</strong> bij "Toolbox email".
        </div>
      )}

      {toolboxen.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#f8fafc', borderRadius: 12 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
          <p style={{ color: '#64748b', margin: 0 }}>Nog geen toolboxen geüpload</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {toolboxen.map(tb => (
            <div key={tb.id} onClick={() => openDetail(tb)} style={{
              background: 'white', borderRadius: 12, padding: 16,
              border: '1px solid #e2e8f0', cursor: 'pointer',
              display: 'flex', gap: 16, alignItems: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: '#1e3a5f', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, flexShrink: 0
              }}>📚</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{tb.titel}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  {new Date(tb.created_at).toLocaleDateString('nl-NL')} · {afdelingNamen(tb.afdeling_ids)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#00D4AA' }}>{tb.aantal_getekend || 0}</div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>getekend</div>
                {tb.verzonden && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ verzonden</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 16px' }}>📚 Nieuwe Toolbox</h2>
            <form onSubmit={handleUpload}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Titel *</label>
                <input type="text" value={uploadData.titel} onChange={(e) => setUploadData({...uploadData, titel: e.target.value})} required
                  style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Omschrijving</label>
                <textarea value={uploadData.omschrijving} onChange={(e) => setUploadData({...uploadData, omschrijving: e.target.value})} rows="3"
                  style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Voor welke afdelingen?</label>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Niets aanvinken = iedereen ziet hem</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {afdelingen.map(afd => (
                    <label key={afd.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: uploadData.afdeling_ids.includes(afd.id) ? '#1e3a5f' : '#f1f5f9', color: uploadData.afdeling_ids.includes(afd.id) ? 'white' : '#475569', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={uploadData.afdeling_ids.includes(afd.id)} onChange={() => toggleAfdeling(afd.id)} style={{ margin: 0 }} />
                      {afd.naam}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>PDF bestand *</label>
                <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={(e) => setUploadFile(e.target.files[0])} required
                  style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                {uploadFile && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowUploadModal(false)} className="btn-secondary" disabled={uploading}>Annuleren</button>
                <button type="submit" className="btn-primary" disabled={uploading}>{uploading ? 'Uploaden...' : '📤 Uploaden'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selected && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 700, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <h2 style={{ margin: '0 0 4px' }}>{selected.titel}</h2>
                <p style={{ color: '#64748b', margin: 0, fontSize: 13 }}>
                  {new Date(selected.created_at).toLocaleDateString('nl-NL')} · {afdelingNamen(selected.afdeling_ids)}
                </p>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>

            {selected.omschrijving && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, margin: '12px 0', fontSize: 14, whiteSpace: 'pre-wrap' }}>
                {selected.omschrijving}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, margin: '16px 0', flexWrap: 'wrap' }}>
              <button onClick={() => openPdf(selected.id)} className="btn-secondary">📄 Bekijk PDF</button>
              <button onClick={() => openPdfVolledig(selected.id)} className="btn-secondary">📑 PDF + Handtekeningen</button>
              <button onClick={() => handleDelete(selected.id)} className="btn-secondary" style={{ color: '#dc2626' }}>🗑️ Verwijder</button>
            </div>

            <h3 style={{ margin: '20px 0 8px', fontSize: 14, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Ondertekenaars ({detail?.handtekeningen?.length || 0})
            </h3>

            {detail?.handtekeningen?.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {detail.handtekeningen.map(h => (
                  <div key={h.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <img src={h.handtekening_data} alt="handtekening" style={{ height: 40, width: 80, objectFit: 'contain', background: 'white', borderRadius: 4, border: '1px solid #e2e8f0' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{h.naam}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {h.afdeling_naam || '-'} · {new Date(h.getekend_op).toLocaleString('nl-NL')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>Nog niemand heeft getekend.</p>
            )}

            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>📧 Versturen</h3>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                {defaultEmail
                  ? <>Standaard: <strong>{defaultEmail}</strong></>
                  : <span style={{ color: '#dc2626' }}>Geen vast email ingesteld</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  placeholder={defaultEmail || "Email-adres"}
                  value={emailOverride}
                  onChange={(e) => setEmailOverride(e.target.value)}
                  style={{ flex: 1, padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                />
                <button onClick={handleVerstuur} className="btn-primary" disabled={verzendenBezig}>
                  {verzendenBezig ? 'Versturen...' : '📧 Verstuur'}
                </button>
              </div>
              {selected.verzonden && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 6 }}>
                  ✓ Eerder verzonden naar {selected.verzonden_naar} op {new Date(selected.verzonden_op).toLocaleString('nl-NL')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Toolboxen;
